
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Named Label Chip Component
 *
 * @file NamedLabelChip.jsx
 * @author Murhsid Ahamedh
 */

import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CustomCard from './CustomCard';

const Header = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer'
  }
});

const Content = styled(Box)({
  '&&': {
    marginTop: 12
  }
});

const RightSection = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }
});

const CustomAccordion = (props) => {
  const {
    title,
    right,
    children,
    defaultExpanded,
    onToggle,
    style,
    className,
    headerStyle,
    contentStyle,
    backgroundColor,
    cardStyle,
    caretColor
  } = props;

  const [expanded, setExpanded] = React.useState(Boolean(defaultExpanded));

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (onToggle) onToggle(next);
  };

  const arrowStyle = {
    color: caretColor || '#5A3FD4',
    fontSize: 24
  };

  return (
    <CustomCard backgroundColor={backgroundColor} style={{ padding: 12, borderRadius: 12, ...style, ...(cardStyle || {}) }} className={className}>
      <Header
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={handleToggle}
        onKeyPress={(e) => { if (e.key === 'Enter') handleToggle(); }}
        style={headerStyle}
      >
        <Box>{title}</Box>
        <RightSection>
          {right}
          {expanded ? <KeyboardArrowUpIcon style={arrowStyle} /> : <KeyboardArrowDownIcon style={arrowStyle} />}
        </RightSection>
      </Header>

      {expanded ? (
        <Content style={contentStyle}>
          {children}
        </Content>
      ) : null}
    </CustomCard>
  );
};

CustomAccordion.propTypes = {
  title: PropTypes.node.isRequired,
  right: PropTypes.node,
  children: PropTypes.node,
  defaultExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  style: PropTypes.object,
  className: PropTypes.string,
  headerStyle: PropTypes.object,
  contentStyle: PropTypes.object,
  backgroundColor: PropTypes.string,
  cardStyle: PropTypes.object,
  caretColor: PropTypes.string
};

CustomAccordion.defaultProps = {
  right: null,
  children: null,
  defaultExpanded: false,
  onToggle: undefined,
  style: {},
  className: '',
  headerStyle: {},
  contentStyle: {},
  backgroundColor: '',
  cardStyle: undefined,
  caretColor: undefined
};

export default CustomAccordion;
